import React, { ComponentProps, FC, MutableRefObject, useCallback, useState } from 'react';
import Typography from '../typography';

export interface TextareaProps extends Omit<ComponentProps<'textarea'>, 'onChange' | 'key'> {
  label?: string;
  labelDesc?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  innerRef?: MutableRefObject<HTMLTextAreaElement>;
  error?: string;
  errorMessage?: string;
  rows?: number;
  required?: boolean;
}

const Textarea: FC<TextareaProps> = ({
  label,
  labelDesc,
  onChange,
  innerRef,
  value: valueProp,
  error,
  errorMessage,
  rows = 3,
  required = false,
  className = '',
  id,
  placeholder,
  ...props
}) => {
  const [value, setValue] = useState(valueProp ?? '');
  const [isTouched, setIsTouched] = useState(false);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(event);
      setValue(event.target.value);
    },
    [onChange],
  );

  const handleBlur = () => setIsTouched(true);

  const textareaClassName = [
    'placeholder:body-text-small w-full resize-none rounded-sm border border-stroke-primary text-primary-black outline-none placeholder:text-text-tertiary focus:border-stroke-primary focus:ring-0',
    error ? 'border-feedback-error focus:border-feedback-error' : '',
    className,
  ].join(' ');

  return (
    <div className="flex flex-col gap-12">
      {label && (
        <label className="body-text-small font-medium" htmlFor={id}>
          {label}
          {required && `*`}
          {labelDesc && <span className="body-text-x-small ml-4 text-text-tertiary">{labelDesc}</span>}
        </label>
      )}
      <textarea
        id={id}
        className={textareaClassName}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={innerRef}
        {...props}
      />
      {(error || errorMessage) && isTouched && (
        <Typography className="mt-2 text-12 font-medium leading-[16px] text-feedback-error" as="span">
          {error ?? errorMessage}
        </Typography>
      )}
    </div>
  );
};

export default Textarea;
